import React, { useEffect, useMemo } from 'react';
import { mutatorAction } from 'satcheljs';
import { useAssetId, AssetId } from '../hooks/useAssetId';
import type { AssetCommand } from './AssetCommand';
import assetCommandsStore from './assetCommandsStore';

const DisableAssetCommandsContext = React.createContext<boolean>(false);
DisableAssetCommandsContext.displayName = 'DisableAssetCommandsContext';

/**
 * When rendering an asset that should not interact with asset commands, set this context to true.
 */
export const DisableAssetCommandsProvider = DisableAssetCommandsContext.Provider;

/**
 * Registers a set of commands supported by an asset instance.
 */
export default function useAssetCommands(commands: AssetCommand[], dependencies: any[]) {
    const assetId = useAssetId();
    const disableAssetCommands = React.useContext(DisableAssetCommandsContext);
    const memoCommands = useMemo(() => commands, dependencies);
    useEffect(() => {
        if (!disableAssetCommands) {
            setAssetCommands(assetId, memoCommands);
        }
        return () => {
            if (!disableAssetCommands) {
                deleteAssetCommands(assetId);
            }
        };
    }, [assetId, memoCommands, disableAssetCommands]);
}

const setAssetCommands = mutatorAction(
    'setAssetCommands',
    (assetId: AssetId, commands: AssetCommand[]) => {
        assetCommandsStore.assetCommandsByAssetId.set(assetId, commands);
    }
);

const deleteAssetCommands = mutatorAction('deleteAssetCommands', (assetId: AssetId) => {
    assetCommandsStore.assetCommandsByAssetId.delete(assetId);
});
